import { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  Play, Zap, Heart, Flame,
  AlertCircle, RefreshCcw, CheckCircle, RefreshCw,
  ChevronRight, X, Lightbulb,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Backward-compat helper: flat ya da nested vimeo alanlarini destekler.
 * @param {Object} lesson
 * @returns {string|null}
 */
function getVideoUrl(lesson) {
  return lesson.vimeo_url || lesson.video?.vimeo_url || null;
}
function getVimeoId(lesson) {
  return lesson.vimeo_video_id || lesson.video?.vimeo_video_id || null;
}

/**
 * VideoLesson — video stream + opsiyonel AI quiz akisi.
 *
 * @param {{ lesson: import('./LessonDispatcher').Lesson, courseId: string, onComplete: Function, completedLessonIds: string[], lessons: Object[], nextLesson: Object|null }} props
 */
export default function VideoLesson({
  lesson,
  courseId,
  onComplete,
  completedLessonIds: _completedLessonIds = [],
  lessons: _lessons = [],
  nextLesson = null,
}) {
  const { getField, lang } = useLanguage();
  const { authHeaders } = useAuth();

  const lessonId = lesson.lesson_id;

  // Video stream
  const [videoStream, setVideoStream] = useState(null);
  const [videoStreamLoading, setVideoStreamLoading] = useState(false);
  const [videoStreamError, setVideoStreamError] = useState(null);
  const videoRefetchCountRef = useRef(0);
  const videoElementRef = useRef(null);

  // Watch progress
  const [_videoWatchPercent, setVideoWatchPercent] = useState(0);
  const videoWatchPercentRef = useRef(0);

  // Duolingo-style AI quiz states
  const [showVideo, setShowVideo] = useState(true);
  const [_generating, _setGenerating] = useState(false);
  const [questions, _setQuestions] = useState(lesson?.quiz_questions || []);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [fillAnswer, setFillAnswer] = useState('');
  const [answerResult, setAnswerResult] = useState(null);
  const [totalXpEarned, setTotalXpEarned] = useState(0);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [lives, setLives] = useState(5);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [comboAnim, setComboAnim] = useState(false);
  const [shakeAnim, setShakeAnim] = useState(false);
  const [_difficulty, _setDifficulty] = useState('medium');

  // Fetch video stream
  const fetchVideoStream = useCallback(async () => {
    if (!lessonId) return;
    setVideoStreamLoading(true);
    setVideoStreamError(null);
    try {
      const res = await axios.get(`${API}/lessons/${lessonId}/video-stream`, { headers: authHeaders() });
      setVideoStream(res.data);
      videoRefetchCountRef.current = 0;
    } catch (err) {
      const status = err?.response?.status;
      const code = err?.response?.data?.detail || err?.response?.data?.error_code || '';
      if (status === 403 && (String(code).includes('not_enrolled') || String(err?.response?.data?.message || '').includes('not_enrolled'))) {
        setVideoStreamError('not_enrolled');
      } else if (status === 401) {
        setVideoStreamError('unauthorized');
      } else if (status === 429) {
        setVideoStreamError('rate_limited');
      } else if (status === 404) {
        setVideoStreamError('not_found');
      } else if (status >= 500) {
        setVideoStreamError('vimeo_unavailable');
      } else {
        setVideoStreamError('unknown');
      }
    } finally {
      setVideoStreamLoading(false);
    }
  }, [lessonId, authHeaders]);

  useEffect(() => {
    if (!getVideoUrl(lesson) && !getVimeoId(lesson)) return;
    fetchVideoStream();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesson.lesson_id]);

  // Restore previous progress
  useEffect(() => {
    axios.get(`${API}/enrollments/${courseId}/video-progress/${lessonId}`, { headers: authHeaders() })
      .then(res => {
        const prev = res.data?.watch_percent || 0;
        if (prev > videoWatchPercentRef.current) {
          videoWatchPercentRef.current = prev;
          setVideoWatchPercent(prev);
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId]);

  // Periodic progress report
  useEffect(() => {
    const interval = setInterval(() => {
      const pct = videoWatchPercentRef.current;
      if (pct > 0) {
        axios.post(`${API}/enrollments/${courseId}/mark-video-progress`,
          { lesson_id: lessonId, watch_percent: pct },
          { headers: authHeaders() }
        ).catch(() => {});
      }
    }, 15000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId]);

  const handleVideoTimeUpdate = useCallback((e) => {
    const el = e.target;
    if (!el.duration || el.duration === 0) return;
    const p = Math.floor((el.currentTime / el.duration) * 100);
    if (p > videoWatchPercentRef.current) {
      videoWatchPercentRef.current = p;
      setVideoWatchPercent(p);
    }
  }, []);

  const handleVideoEnded = useCallback(() => {
    videoWatchPercentRef.current = 100;
    setVideoWatchPercent(100);
  }, []);

  const handleVideoError = useCallback(() => {
    if (videoRefetchCountRef.current < 1) {
      videoRefetchCountRef.current += 1;
      fetchVideoStream();
    }
  }, [fetchVideoStream]);

  // Quiz helpers
  const startQuizSession = useCallback(async () => {
    try {
      const res = await axios.post(`${API}/quiz/session/start`,
        { lesson_id: lessonId, course_id: courseId },
        { headers: authHeaders() }
      );
      setSessionId(res.data.session_id);
      setLives(5);
      setStreak(0);
      setBestStreak(0);
    } catch (err) {
      console.error('Session start error:', err);
    }
  }, [lessonId, courseId, authHeaders]);


  const checkAnswer = async () => {
    const currentQ = questions[currentQuestionIndex];
    if (!currentQ) return;
    const answer = currentQ.type === 'fill_blank' ? fillAnswer : selectedAnswer;
    if (!answer) return;
    try {
      const res = await axios.post(`${API}/quiz/answer`, {
        lesson_id: lessonId,
        question_index: currentQuestionIndex,
        answer,
        session_id: sessionId,
      }, { headers: authHeaders() });
      setAnswerResult(res.data);
      if (res.data.correct) {
        setCorrectCount(prev => prev + 1);
        setTotalXpEarned(prev => prev + (res.data.xp_earned || 0));
        setStreak(res.data.current_streak || 0);
        setBestStreak(prev => Math.max(prev, res.data.current_streak || 0));
        if (res.data.current_streak >= 3) {
          setComboAnim(true);
          setTimeout(() => setComboAnim(false), 1500);
        }
      } else {
        setLives(res.data.lives_remaining);
        setStreak(0);
        setShakeAnim(true);
        setTimeout(() => setShakeAnim(false), 600);
        if (res.data.game_over) setGameOver(true);
      }
    } catch (err) {
      console.error('Answer error:', err);
    }
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setSelectedAnswer(null);
      setFillAnswer('');
      setAnswerResult(null);
      setShowHint(false);
    } else {
      setQuizCompleted(true);
      if (sessionId) {
        axios.post(`${API}/quiz/session/complete`, { session_id: sessionId }, { headers: authHeaders() }).catch(() => {});
      }
    }
  };

  const retryQuiz = () => {
    setCurrentQuestionIndex(0);
    setSelectedAnswer(null);
    setFillAnswer('');
    setAnswerResult(null);
    setQuizCompleted(false);
    setCorrectCount(0);
    setTotalXpEarned(0);
    setGameOver(false);
    setLives(5);
    setStreak(0);
    setShowHint(false);
    startQuizSession();
  };

  const currentQuestion = questions[currentQuestionIndex];
  const hasVideo = !!(getVideoUrl(lesson) || getVimeoId(lesson));

  return (
    <>
      {/* Combo Animation Overlay */}
      <AnimatePresence>
        {comboAnim && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.5 }}
            className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none"
          >
            <div className="bg-gradient-to-r from-orange-400 to-yellow-400 text-white px-8 py-4 rounded-2xl shadow-2xl">
              <div className="flex items-center gap-3">
                <Flame size={32} />
                <div>
                  <p className="font-display text-2xl font-bold">{streak}x COMBO!</p>
                  <p className="text-sm font-bold opacity-90">+{Math.min(streak * 2, 10)} bonus XP</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Quiz header bar: lives + streak (XP gostergesi bu sayfadan kaldirildi) */}
      {!showVideo && questions.length > 0 && (
        <div className="bg-white border-b-2 border-[var(--border-light)] px-4 py-2 -mx-4 -mt-0 mb-3 flex items-center justify-end gap-4">
          <div className="flex items-center gap-1" data-testid="lives-display">
            {[...Array(5)].map((_, i) => (
              <Heart
                key={i}
                size={18}
                className={`transition-all duration-300 ${i < lives ? 'text-red-500 fill-red-500' : 'text-gray-300'}`}
                style={i >= lives ? { transform: 'scale(0.8)' } : {}}
              />
            ))}
          </div>
          {streak > 0 && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="flex items-center gap-1 bg-orange-100 px-2 py-1 rounded-lg"
              data-testid="streak-display"
            >
              <Flame size={14} className="text-orange-500" />
              <span className="text-xs font-bold text-orange-600">{streak}</span>
            </motion.div>
          )}
        </div>
      )}

      {/* Video/Quiz Toggle */}
      {hasVideo && questions.length > 0 && (
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setShowVideo(true)}
            className={`px-4 py-2 rounded-xl font-bold text-sm border-2 transition-all ${showVideo ? 'bg-[var(--brand-primary)] text-white border-[var(--brand-primary-shadow)]' : 'bg-white border-[var(--border-light)]'}`}
            data-testid="show-video-btn"
          >
            <Play size={16} className="inline mr-1" /> Video
          </button>
          <button
            onClick={() => setShowVideo(false)}
            className={`px-4 py-2 rounded-xl font-bold text-sm border-2 transition-all ${!showVideo ? 'bg-[var(--brand-secondary)] text-[var(--text-main)] border-[var(--brand-secondary-shadow)]' : 'bg-white border-[var(--border-light)]'}`}
            data-testid="show-quiz-btn"
          >
            <Zap size={16} className="inline mr-1" /> Quiz
          </button>
        </div>
      )}

      {/* ---- VIDEO PANEL ---- */}
      {showVideo && hasVideo && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-8">
          {/* overflow-hidden KULLANILMIYOR: WebKit'te border-radius + overflow:hidden
              composited iframe katmanini kirpip videoyu gorunmez birakabiliyor
              ("ses var goruntu yok"). Kose yuvarlakligi cocuklara tasindi.
              Oynatici absolute inset-0: yuzde-yukseklik cozumune gerek kalmaz. */}
          <div className="rounded-2xl border-2 border-[var(--border-light)] aspect-video bg-black relative">
            {videoStreamLoading && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900 gap-3 rounded-2xl" data-testid="video-stream-loading">
                <div className="w-10 h-10 border-4 border-[var(--brand-primary)] border-t-transparent rounded-full animate-spin" />
                <span className="text-white text-sm font-semibold">
                  {lang === 'tr' ? 'Video yükleniyor...' : 'Loading video...'}
                </span>
              </div>
            )}
            {!videoStreamLoading && videoStreamError && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900 gap-4 rounded-2xl px-6 text-center" data-testid="video-stream-error">
                <AlertCircle size={40} className="text-rose-400" />
                <p className="text-white font-bold text-sm">
                  {videoStreamError === 'not_enrolled'
                    ? (lang === 'tr' ? 'Bu kursa kayıtlı değilsiniz.' : 'You are not enrolled in this course.')
                    : videoStreamError === 'unauthorized'
                    ? (lang === 'tr' ? 'Oturum süreniz doldu, lütfen tekrar giriş yapın.' : 'Your session has expired, please log in again.')
                    : videoStreamError === 'rate_limited'
                    ? (lang === 'tr' ? 'Çok fazla istek, lütfen bir süre bekleyin.' : 'Too many requests, please wait a moment.')
                    : videoStreamError === 'not_found'
                    ? (lang === 'tr' ? 'Video bulunamadı.' : 'Video not found.')
                    : videoStreamError === 'vimeo_unavailable'
                    ? (lang === 'tr' ? 'Video servisi geçici olarak kullanılamıyor.' : 'Video service is temporarily unavailable.')
                    : (lang === 'tr' ? 'Video yüklenemedi.' : 'Could not load video.')}
                </p>
                {videoStreamError !== 'not_enrolled' && videoStreamError !== 'unauthorized' && (
                  <button
                    onClick={fetchVideoStream}
                    className="flex items-center gap-2 px-4 py-2 bg-[var(--brand-primary)] text-white rounded-xl font-bold text-sm hover:opacity-90 transition"
                    data-testid="video-stream-retry-btn"
                  >
                    <RefreshCcw size={16} />
                    {lang === 'tr' ? 'Tekrar Dene' : 'Retry'}
                  </button>
                )}
              </div>
            )}
            {!videoStreamLoading && !videoStreamError && videoStream?.stream_url && (
              <video
                ref={videoElementRef}
                key={videoStream.stream_url}
                src={videoStream.stream_url}
                poster={videoStream.thumbnail_url || undefined}
                controls
                playsInline
                // iOS <10 legacy; playsInline modern iOS surumlerini zaten kapsar.
                // React bu ozelligi tanimadigi icin lint kurali susturuldu.
                // eslint-disable-next-line react/no-unknown-property
                webkit-playsinline="true"
                preload="metadata"
                onTimeUpdate={handleVideoTimeUpdate}
                onEnded={handleVideoEnded}
                onError={handleVideoError}
                className="absolute inset-0 w-full h-full rounded-2xl"
                data-testid="lesson-video-element"
                title={getField(lesson, 'title')}
              >
                {lang === 'tr' ? 'Tarayiciniz HTML5 videoyu desteklemiyor.' : 'Your browser does not support HTML5 video.'}
              </video>
            )}
            {/* External embed fallback: backend stream_url=null + is_external=true donerken
                embed_proxy_url veya vimeo_embed_url ile iframe goster */}
            {!videoStreamLoading && !videoStreamError && videoStream?.is_external && !videoStream?.stream_url && (
              <iframe
                key={videoStream.embed_proxy_url || videoStream.vimeo_embed_url}
                src={videoStream.embed_proxy_url || videoStream.vimeo_embed_url}
                className="absolute inset-0 w-full h-full rounded-2xl"
                style={{ border: 'none' }}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title={getField(lesson, 'title')}
                data-testid="lesson-video-iframe"
                onLoad={() => {
                  // iframe yuklenince watch percent'i manual takip edemeyiz;
                  // 100'e set etmek yerine "started watching" sinyali ver (20%)
                  if (videoWatchPercentRef.current < 20) {
                    videoWatchPercentRef.current = 20;
                    setVideoWatchPercent(20);
                  }
                }}
              />
            )}
          </div>

          {/* Start quiz button */}
          {questions.length > 0 && (
            <div className="mt-4 text-center">
              <button
                onClick={() => { setShowVideo(false); startQuizSession(); }}
                className="btn-secondary px-8 py-3 text-lg"
                data-testid="start-quiz-btn"
              >
                {lang === 'tr' ? 'Quiz\'e Başla' : 'Start Quiz'}
              </button>
            </div>
          )}
        </motion.div>
      )}

      {/* ---- GAME OVER ---- */}
      {gameOver && !showVideo && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="card-clay p-8 text-center mb-6"
          data-testid="game-over"
        >
          <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
            <Heart size={40} className="text-red-400" />
          </div>
          <h2 className="font-display text-3xl font-bold mb-2 text-red-500">
            {lang === 'tr' ? 'Canlar Bitti!' : 'No Lives Left!'}
          </h2>
          <p className="text-[var(--text-muted)] font-semibold mb-2">
            {correctCount}/{questions.length} {lang === 'tr' ? 'doğru cevap' : 'correct answers'}
          </p>
          <p className="text-sm text-[var(--text-muted)] mb-6">
            {lang === 'tr' ? 'Endislenme, tekrar deneyebilirsin!' : "Don't worry, you can try again!"}
          </p>
          <div className="inline-flex items-center gap-2 bg-[#FFF4CC] px-6 py-3 rounded-xl mb-6">
            <Zap size={20} className="text-[var(--brand-secondary-shadow)]" />
            <span className="font-bold text-lg">+{totalXpEarned} XP</span>
          </div>
          <div className="flex justify-center gap-4">
            <button onClick={retryQuiz} className="btn-primary px-8 py-3 flex items-center gap-2" data-testid="retry-quiz-btn">
              <RefreshCw size={18} /> {lang === 'tr' ? 'Tekrar Dene' : 'Try Again'}
            </button>
            {hasVideo && (
              <button onClick={() => setShowVideo(true)} className="btn-tactile px-6 py-3 bg-white border-[var(--border-light)]">
                {lang === 'tr' ? 'Videoyu Izle' : 'Watch Video'}
              </button>
            )}
          </div>
        </motion.div>
      )}

      {/* ---- QUIZ QUESTIONS ---- */}
      {!showVideo && questions.length > 0 && !quizCompleted && !gameOver && currentQuestion && (
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestionIndex}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className={`card-clay p-8 mb-6 ${shakeAnim ? 'animate-shake' : ''}`}
            data-testid="quiz-container"
          >
            <div className="flex items-center justify-between mb-6">
              <span className="text-sm font-bold text-[var(--text-muted)]">
                {lang === 'tr' ? 'Soru' : 'Question'} {currentQuestionIndex + 1}/{questions.length}
              </span>
              <div className="flex items-center gap-2">
                {currentQuestion.difficulty && (
                  <span className={`px-2 py-0.5 rounded-lg text-[10px] font-bold ${
                    currentQuestion.difficulty === 1 ? 'bg-green-100 text-green-700' :
                    currentQuestion.difficulty === 2 ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {currentQuestion.difficulty === 1 ? (lang === 'tr' ? 'Kolay' : 'Easy') :
                     currentQuestion.difficulty === 2 ? (lang === 'tr' ? 'Orta' : 'Medium') :
                     (lang === 'tr' ? 'Zor' : 'Hard')}
                  </span>
                )}
                <span className="bg-gray-100 px-3 py-1 rounded-lg text-xs font-bold text-[var(--text-muted)]">
                  {currentQuestion.type === 'multiple_choice' ? (lang === 'tr' ? 'Çoktan Seçmeli' : 'Multiple Choice') :
                   currentQuestion.type === 'fill_blank' ? (lang === 'tr' ? 'Boşluk Doldurma' : 'Fill in the Blank') :
                   (lang === 'tr' ? 'Doğru/Yanlış' : 'True/False')}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-1 mb-4">
              <Zap size={14} className="text-[var(--brand-secondary)]" />
              <span className="text-xs font-bold text-[var(--brand-secondary-shadow)]">
                +{currentQuestion.xp_value || 10} XP
              </span>
            </div>

            <h3 className="font-display text-xl font-bold mb-8">{currentQuestion.question}</h3>

            {/* Multiple Choice / True-False */}
            {currentQuestion.type !== 'fill_blank' && (
              <div className="space-y-3 mb-8">
                {(currentQuestion.options || []).map((option, i) => {
                  let classes = 'quiz-option';
                  if (answerResult) {
                    if (option.toLowerCase() === answerResult.correct_answer?.toLowerCase()) classes += ' correct';
                    else if (option === selectedAnswer && !answerResult.correct) classes += ' incorrect';
                  } else if (option === selectedAnswer) {
                    classes += ' selected';
                  }
                  return (
                    <motion.button
                      key={i}
                      whileTap={!answerResult ? { scale: 0.98 } : {}}
                      onClick={() => !answerResult && setSelectedAnswer(option)}
                      className={`w-full text-left ${classes}`}
                      disabled={!!answerResult}
                      data-testid={`quiz-option-${i}`}
                    >
                      <span className="mr-3 inline-flex items-center justify-center w-8 h-8 rounded-lg border-2 border-current text-sm font-bold">
                        {String.fromCharCode(65 + i)}
                      </span>
                      {option}
                    </motion.button>
                  );
                })}
              </div>
            )}

            {/* Fill in the Blank */}
            {currentQuestion.type === 'fill_blank' && (
              <div className="mb-8">
                <input
                  type="text"
                  value={fillAnswer}
                  onChange={(e) => setFillAnswer(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !answerResult && fillAnswer && checkAnswer()}
                  disabled={!!answerResult}
                  placeholder={lang === 'tr' ? 'Cevabinizi yazin...' : 'Type your answer...'}
                  className={`w-full px-5 py-4 rounded-xl border-3 text-lg font-bold text-center focus:outline-none transition-all ${
                    answerResult
                      ? answerResult.correct
                        ? 'border-[var(--brand-success)] bg-green-50'
                        : 'border-[var(--brand-danger)] bg-red-50'
                      : 'border-[var(--border-light)] bg-gray-50 focus:border-[var(--brand-primary)]'
                  }`}
                  data-testid="fill-blank-input"
                />
                {answerResult && !answerResult.correct && (
                  <p className="text-center mt-2 text-sm font-bold text-[var(--brand-success)]">
                    {lang === 'tr' ? 'Doğru cevap: ' : 'Correct answer: '}{answerResult.correct_answer}
                  </p>
                )}
              </div>
            )}

            {/* Hint */}
            {currentQuestion.hint && !answerResult && (
              <div className="mb-4">
                {!showHint ? (
                  <button
                    onClick={() => setShowHint(true)}
                    className="text-sm font-semibold text-[var(--brand-primary)] flex items-center gap-1 hover:underline"
                    data-testid="show-hint-btn"
                  >
                    <Lightbulb size={14} /> {lang === 'tr' ? 'İpucu Göster' : 'Show hint'}
                  </button>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="bg-blue-50 border-2 border-blue-200 rounded-xl p-3 flex items-start gap-2"
                  >
                    <Lightbulb size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
                    <p className="text-sm font-semibold text-blue-700">{currentQuestion.hint}</p>
                  </motion.div>
                )}
              </div>
            )}

            {/* Answer Result */}
            {answerResult && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-4 rounded-xl mb-6 ${answerResult.correct ? 'bg-green-50 border-2 border-[var(--brand-success)]' : 'bg-red-50 border-2 border-[var(--brand-danger)]'}`}
                data-testid="answer-result"
              >
                <div className="flex items-center gap-2 mb-1">
                  {answerResult.correct
                    ? <CheckCircle size={20} className="text-[var(--brand-success)]" />
                    : <X size={20} className="text-[var(--brand-danger)]" />}
                  <span className="font-bold text-lg">
                    {answerResult.correct ? (lang === 'tr' ? 'Doğru!' : 'Correct!') : (lang === 'tr' ? 'Yanlış!' : 'Incorrect!')}
                  </span>
                  {answerResult.correct && answerResult.xp_earned > 0 && (
                    <span className="ml-auto bg-[var(--brand-secondary)] px-2 py-0.5 rounded-lg text-xs font-bold">
                      +{answerResult.xp_earned} XP
                      {answerResult.streak_bonus > 0 && ` (streak +${answerResult.streak_bonus})`}
                    </span>
                  )}
                </div>
                <p className="text-sm font-medium ml-7">{answerResult.explanation}</p>
              </motion.div>
            )}

            {/* Actions */}
            <div className="flex justify-end">
              {!answerResult ? (
                <button
                  onClick={checkAnswer}
                  disabled={currentQuestion.type === 'fill_blank' ? !fillAnswer : !selectedAnswer}
                  className="btn-primary px-8 py-3 disabled:opacity-50"
                  data-testid="check-answer-btn"
                >
                  {lang === 'tr' ? 'Kontrol Et' : 'Check'}
                </button>
              ) : (
                <button
                  onClick={nextQuestion}
                  className="btn-success px-8 py-3"
                  data-testid="next-question-btn"
                >
                  {currentQuestionIndex < questions.length - 1
                    ? (lang === 'tr' ? 'Devam' : 'Continue')
                    : (lang === 'tr' ? 'Bitir' : 'Finish')}
                </button>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      )}

      {/* ---- QUIZ COMPLETED ---- */}
      {quizCompleted && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="card-clay p-8 text-center mb-6"
          data-testid="quiz-complete"
        >
          <div className="w-20 h-20 rounded-full bg-[var(--brand-secondary)] flex items-center justify-center mx-auto mb-6">
            <Zap size={40} strokeWidth={2.5} className="text-[var(--text-main)]" />
          </div>
          <h2 className="font-display text-3xl font-bold mb-2">
            {correctCount === questions.length
              ? (lang === 'tr' ? 'Mükemmel!' : 'Perfect!')
              : correctCount >= questions.length * 0.7
              ? (lang === 'tr' ? 'Harika!' : 'Great!')
              : (lang === 'tr' ? 'İyi deneme!' : 'Good try!')}
          </h2>
          <p className="text-[var(--text-muted)] font-semibold mb-6">
            {correctCount}/{questions.length} {lang === 'tr' ? 'doğru cevap' : 'correct answers'}
          </p>
          <div className="grid grid-cols-3 gap-4 mb-8 max-w-sm mx-auto">
            <div className="bg-[#FFF4CC] p-3 rounded-xl">
              <Zap size={20} className="mx-auto text-[var(--brand-secondary-shadow)] mb-1" />
              <p className="font-bold text-lg">{totalXpEarned}</p>
              <p className="text-[10px] font-bold text-[var(--text-muted)]">XP</p>
            </div>
            <div className="bg-orange-50 p-3 rounded-xl">
              <Flame size={20} className="mx-auto text-orange-500 mb-1" />
              <p className="font-bold text-lg">{bestStreak}</p>
              <p className="text-[10px] font-bold text-[var(--text-muted)]">{lang === 'tr' ? 'En Iyi Seri' : 'Best Streak'}</p>
            </div>
            <div className="bg-red-50 p-3 rounded-xl">
              <Heart size={20} className="mx-auto text-red-500 fill-red-500 mb-1" />
              <p className="font-bold text-lg">{lives}/5</p>
              <p className="text-[10px] font-bold text-[var(--text-muted)]">{lang === 'tr' ? 'Kalan Can' : 'Lives Left'}</p>
            </div>
          </div>
          <div className="flex justify-center gap-4">
            {nextLesson && (
              <button
                onClick={() => onComplete && onComplete({ navigateTo: nextLesson })}
                className="btn-primary px-8 py-3"
                data-testid="next-lesson-btn"
              >
                {lang === 'tr' ? 'Sonraki Ders' : 'Next Lesson'} <ChevronRight size={18} className="inline" />
              </button>
            )}
            <button onClick={retryQuiz} className="btn-tactile px-6 py-3 bg-white border-[var(--border-light)] flex items-center gap-2" data-testid="retry-quiz-from-complete-btn">
              <RefreshCw size={16} /> {lang === 'tr' ? 'Tekrar' : 'Retry'}
            </button>
          </div>
        </motion.div>
      )}

    </>
  );
}
